import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { cn } from 'support/helpers/cn/cn';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { WebEDIRowData } from 'support/types/webediTable';
import { useValue } from 'components/WebEDI/helpers';

import { overflowTooltipTarget } from '../../../components/OverflowTooltip/overflowTooltipTarget';
import { CellProps } from '../../types';
import { useCellClassNames, useEnumerableConfigurations, useLineItemsTransportUnitsConfig } from '../cellHelpers';
import { ViewWrapperCell } from './ViewWrapperCell';

export type Props = CellContext<WebEDIRowData, unknown> &
  Pick<CellProps, 'fieldId' | 'isHighlighted' | 'diffStatus' | 'originalValue' | 'wasAIEdited'>;
export const ViewEnumerableCell = ({
  row,
  cell,
  column,
  table,
  isHighlighted,
  fieldId,
  diffStatus,
  originalValue,
  wasAIEdited,
}: Props) => {
  const { t } = useTranslation();
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const error = service.useItemByFieldPath(fieldId);
  const value = useValue(row.original, fieldId, column.columnDef.meta?.optionalConfig?.expression);
  const {
    columnDef: { meta: columnMeta },
  } = column;
  const { isReadOnly: isTransportUnitReadOnly, isUnassigned: isUnassignedTransportUnit } =
    useLineItemsTransportUnitsConfig(fieldId, row?.original, Boolean(columnMeta?.packingListEnabled));

  const { formattedValue } = useEnumerableConfigurations({
    meta: columnMeta,
    value,
  });
  const { formattedValue: baseFormattedEnum } = useEnumerableConfigurations({
    meta: columnMeta,
    value: originalValue ?? undefined,
  });

  const valueToShow = isUnassignedTransportUnit
    ? t('webedi:line_items.field.transport_unit.unassigned')
    : isTransportUnitReadOnly
      ? t('webedi:line_items.field.transport_unit.not_shipped')
      : formattedValue;

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });

  return (
    <ViewWrapperCell
      fieldId={fieldId}
      isHighlighted={isHighlighted}
      className={cellClassNames}
      meta={columnMeta}
      cell={cell}
      diffStatus={diffStatus}
      originalValue={originalValue}
      formattedOriginalValue={baseFormattedEnum}
      textAlign={columnMeta?.optionalConfig?.textAlign}
      wasAIEdited={wasAIEdited}
      hasWarning={isUnassignedTransportUnit}
    >
      <span className="flex w-full min-w-0 items-center">
        <div
          className={cn('flex w-full min-w-0 gap-x-0.5 overflow-hidden', {
            'justify-start': columnMeta?.optionalConfig?.textAlign === 'left',
          })}
        >
          {!valueToShow && error?.type === 'required' ? (
            t('common:table.errors.requiredField')
          ) : valueToShow ? (
            <span className="flex-initial truncate" {...overflowTooltipTarget}>
              {valueToShow}
            </span>
          ) : (
            <span className={cn({ 'text-gray-400': !error })}>{EMPTY_FIELD}</span>
          )}
        </div>
      </span>
    </ViewWrapperCell>
  );
};
